import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import { CartWishlistProvider } from "./contexts/CartWishlistContext";
import ProtectedRoute from "./routes/ProtectedRoute";
import DashboardProtectedRoute from "./routes/DashboardProtectedRoute";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import SetPasswordModal from "./pages/SetPasswordModal";

import Home from "./pages/Home";
import Jewellery from "./pages/Jewellery";
import TraditionalWestern from "./pages/TraditionalWestern";
import BagsPurses from "./pages/BagsPurses";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Dashboard from "./pages/dashboard/Dashboard";
import DashboardLogin from "./pages/dashboard/DashboardLogin";

import PriceRangePage from "./pages/PriceRangePage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CategoryPage from "./pages/CategoryPage";

import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import Profile from "./pages/Profile";
import WishlistPage from "./pages/WishlistPage";
import Orders from "./pages/Orders";
import ResetPassword from "./pages/ResetPassword";

import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CancellationRefundPolicy from "./pages/CancellationRefundPolicy";
import ShippingPolicy from "./pages/ShippingPolicy";

import { PRODUCTS } from "./data/products";

import "./App.css";

const PRODUCT_PATHS = Object.keys(PRODUCTS);

function AppContent() {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/dashboard');
  const [showSetPassword, setShowSetPassword] = useState(false);

  useEffect(() => {
    // Detect when Supabase redirects back after magic link or signup click.
    // The URL hash contains type=magiclink or type=signup.
    const hash = window.location.hash;
    if (hash && (hash.includes('type=magiclink') || hash.includes('type=signup'))) {
      setShowSetPassword(true);
      // Delay cleaning the hash to guarantee Supabase captures the access_token first!
      setTimeout(() => {
        if (window.location.hash.includes('type=')) {
          window.history.replaceState(null, '', window.location.pathname);
        }
      }, 2000);
    }
  }, []);

  return (
    <div className="appShell">
      {showSetPassword && (
        <SetPasswordModal onClose={() => setShowSetPassword(false)} />
      )}
      {!isDashboard && <Navbar />}

      <Routes>
        {/* Home */}
        <Route path="/" element={<Home />} />

        {/* Main Pages */}
        <Route path="/jewellery" element={<Jewellery />} />
        <Route path="/traditional-western" element={<TraditionalWestern />} />
        <Route path="/bags-purses" element={<BagsPurses />} />

        {/* ✅ Cart + Wishlist + Checkout */}
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/orders" element={<Orders />} />

        {/* Redirects */}
        <Route
          path="/collections/jewellery"
          element={<Navigate to="/jewellery" replace />}
        />
        <Route
          path="/collections/traditional-western"
          element={<Navigate to="/traditional-western" replace />}
        />
        <Route
          path="/collections/bags-purses"
          element={<Navigate to="/bags-purses" replace />}
        />

        {/* Other Pages */}
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard/login" element={<DashboardLogin />} />
        <Route path="/dashboard" element={<DashboardProtectedRoute><Dashboard /></DashboardProtectedRoute>} />
        <Route path="/under99" element={<PriceRangePage />} />
        <Route path="/under99/:category" element={<PriceRangePage />} />
        <Route path="/under499" element={<PriceRangePage />} />
        <Route path="/under499/:category" element={<PriceRangePage />} />
        <Route path="/under999" element={<PriceRangePage />} />
        <Route path="/under999/:category" element={<PriceRangePage />} />
        <Route path="/under1499" element={<PriceRangePage />} />
        <Route path="/under1499/:category" element={<PriceRangePage />} />

        {/* Dynamic main / generic category pages */}
        <Route path="/category/:slug" element={<CategoryPage />} />

        {/* Policy Pages */}
        <Route path="/terms-and-conditions" element={<TermsConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="/cancellation-refund-policy"
          element={<CancellationRefundPolicy />}
        />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Supabase / Dashboard products (dynamic) */}
        <Route path="/product/:id" element={<ProductDetailPage />} />

        {/* Static Product Detail Pages */}
        {PRODUCT_PATHS.map((productId) => (
          <Route
            key={productId}
            path={`/${productId}`}
            element={<ProductDetailPage />}
          />
        ))}

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {!isDashboard && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartWishlistProvider>
          <ScrollToTop />
          <AppContent />
        </CartWishlistProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}